import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
} from 'react'

import LoadingContext from '../../context/Loading/Loading'

import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'

import AuthService from '../../utilities/services/auth.service'
import UserContext from '../../context/User/User'
import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { checkPermission } from '../../utilities/helpers'
import {
  getBuyerTypesForSettings,
  getSystemDefaultBuyerTypes,
  createBuyerType,
  updateBuyerType,
} from '../../utilities/api'
import {
  inferBuyerTypeCategory,
  parentIsComplimentary,
} from '../../utilities/buyerTypeFilters'

import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { LoadingContainer } from '../LoadingContainer'

import './buyerTypesWrapper.scss'

const DEFAULT_ACCENT_FALLBACK = '#64748b'
/** Tag pill color for Standard (left accent stays neutral from API) */
const STANDARD_TAG_ACCENT = '#2563eb'
/** After Standard: match product order; then any other system defaults A–Z */
const DEFAULT_TYPE_ORDER_AFTER_STANDARD = ['comp', 'package', 'group']

function getAttrs(row) {
  return row.attributes ?? row
}

function humanizeKey(key) {
  if (key == null || key === '') return '—'
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function categoryLabel(value) {
  const map = {
    individual: 'Standard',
    group: 'Group',
    comp: 'Comp',
    box_office_comp: 'Box office comp',
  }
  if (value == null || value === '') return '—'
  return map[value] || humanizeKey(value)
}

function displayBuyerTypeName(name) {
  return humanizeKey(name)
}

/** Parse sub_types from API: strings and/or { key, label, shortLabel } */
function normalizeChannelEntries(raw) {
  let arr = raw
  if (typeof raw === 'string') {
    try {
      arr = JSON.parse(raw)
    } catch {
      return []
    }
  }
  if (!Array.isArray(arr)) return []
  return arr.map((item, i) => {
    if (item != null && typeof item === 'object') {
      const key = String(item.key ?? item.id ?? item.slug ?? i)
      const label =
        item.label != null && String(item.label).trim() !== ''
          ? String(item.label)
          : humanizeKey(key)
      const shortLabel =
        item.shortLabel != null && String(item.shortLabel).trim() !== ''
          ? String(item.shortLabel)
          : label.includes(' ')
            ? label.split(/\s+/)[0]
            : label
      return { key, label, shortLabel }
    }
    const key = String(item)
    const label = humanizeKey(key)
    return {
      key,
      label,
      shortLabel: label.includes(' ') ? label.split(/\s+/)[0] : label,
    }
  })
}

function accentForDefaultRow(a) {
  if (a.color && /^#?[0-9a-f]{6}$/i.test(String(a.color).trim())) {
    const c = String(a.color).trim().startsWith('#')
      ? String(a.color).trim()
      : `#${String(a.color).trim()}`
    return c
  }
  return DEFAULT_ACCENT_FALLBACK
}

function sortDefaultTypesForDisplay(rows) {
  return [...rows].sort((x, y) => {
    const ax = String(getAttrs(x).name ?? '').toLowerCase()
    const ay = String(getAttrs(y).name ?? '').toLowerCase()
    if (ax === 'standard' && ay !== 'standard') return -1
    if (ay === 'standard' && ax !== 'standard') return 1
    const ix = DEFAULT_TYPE_ORDER_AFTER_STANDARD.indexOf(ax)
    const iy = DEFAULT_TYPE_ORDER_AFTER_STANDARD.indexOf(ay)
    const sx = ix === -1 ? 999 : ix
    const sy = iy === -1 ? 999 : iy
    if (sx !== sy) return sx - sy
    return ax.localeCompare(ay)
  })
}

function formatParentChannelsLine(entries) {
  if (!entries.length) return ''
  return entries.map((e) => e.shortLabel).join(' · ')
}

function formatChannelsReadableList(entries) {
  if (!entries.length) return ''
  const labels = entries.map((e) => e.label)
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}

function buyerTypeRowKey(row) {
  return String(row.documentId ?? row.id)
}

function resolveParentRow(parentValue, defaultRows) {
  if (parentValue == null || parentValue === '') return null
  const pv = String(parentValue).trim()
  for (const row of defaultRows) {
    const a = getAttrs(row)
    if (String(row.id) === pv || String(row.documentId ?? '') === pv) return row
    if (String(a.abbreviation ?? '').toLowerCase() === pv.toLowerCase())
      return row
    if (String(a.name ?? '').toLowerCase() === pv.toLowerCase()) return row
  }
  return null
}

function resolveParentMeta(managedRow, defaultRows) {
  const a = getAttrs(managedRow)
  const resolved = resolveParentRow(a.parent, defaultRows)
  if (resolved) {
    const da = getAttrs(resolved)
    return {
      label: displayBuyerTypeName(da.name),
      color: accentForDefaultRow(da),
    }
  }
  const defByCat = defaultRows.find((d) => getAttrs(d).category === a.category)
  if (defByCat) {
    const da = getAttrs(defByCat)
    return {
      label: displayBuyerTypeName(da.name),
      color: accentForDefaultRow(da),
    }
  }
  return {
    label: categoryLabel(a.category),
    color: DEFAULT_ACCENT_FALLBACK,
  }
}

function hexToRgba(hex, alpha) {
  const h = String(hex).replace('#', '')
  if (h.length !== 6) return `rgba(100, 116, 139, ${alpha})`
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

function isManagedBuyerType(row) {
  const a = getAttrs(row)
  return !a.systemDefault
}

function PencilIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function BuyerTypesWrapper() {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const organization = AuthService.getOrg()[0]
  const orgUUID = organization?.uuid

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.MANAGE_BUYER_TYPES
    )

  const [systemDefaultRows, setSystemDefaultRows] = useState([])
  const [orgBuyerTypeRows, setOrgBuyerTypeRows] = useState([])
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    name: '',
    abbreviation: '',
    parentKey: '',
  })

  const defaultTypes = useMemo(
    () =>
      sortDefaultTypesForDisplay(
        systemDefaultRows.filter((row) => getAttrs(row).systemDefault === true)
      ),
    [systemDefaultRows]
  )

  const parentTypeOptions = useMemo(
    () =>
      defaultTypes.filter(
        (row) => String(getAttrs(row).name ?? '').toLowerCase() !== 'standard'
      ),
    [defaultTypes]
  )

  const selectedParentRow = useMemo(() => {
    if (!form.parentKey) return null
    return (
      parentTypeOptions.find((r) => buyerTypeRowKey(r) === form.parentKey) ??
      defaultTypes.find((r) => buyerTypeRowKey(r) === form.parentKey) ??
      null
    )
  }, [form.parentKey, parentTypeOptions, defaultTypes])

  const managedTypes = useMemo(
    () => orgBuyerTypeRows.filter(isManagedBuyerType),
    [orgBuyerTypeRows]
  )

  const load = useCallback(() => {
    setIsLoadingData(true)
    const orgFetch = orgUUID
      ? getBuyerTypesForSettings(orgUUID)
      : Promise.resolve({ data: { data: [] } })

    Promise.all([getSystemDefaultBuyerTypes(), orgFetch])
      .then(([sysRes, orgRes]) => {
        setSystemDefaultRows(sysRes?.data?.data ?? [])
        setOrgBuyerTypeRows(orgRes?.data?.data ?? [])
      })
      .catch((err) => console.error(err))
      .finally(() => {
        setIsLoadingData(false)
        hideLoading()
      })
  }, [orgUUID])

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    if (!orgUUID) return
    showLoading()
    load()
  }, [orgPermissionsReady, hasPermission, orgUUID])

  const openAdd = () => {
    setEditingId(null)
    const first = parentTypeOptions[0]
    setForm({
      name: '',
      abbreviation: '',
      parentKey: first ? buyerTypeRowKey(first) : '',
    })
    setShowModal(true)
  }

  const openEdit = (row) => {
    const a = getAttrs(row)
    const resolved = resolveParentRow(a.parent, defaultTypes)
    const resolvedName = resolved
      ? String(getAttrs(resolved).name ?? '').toLowerCase()
      : ''
    const useParent =
      resolved && resolvedName !== 'standard' ? resolved : parentTypeOptions[0]
    setEditingId(row.id)
    setForm({
      name: a.name ?? '',
      abbreviation: a.abbreviation ?? '',
      parentKey: useParent ? buyerTypeRowKey(useParent) : '',
    })
    setShowModal(true)
  }

  const closeModal = () => {
    if (!saving) {
      setShowModal(false)
      setEditingId(null)
    }
  }

  const nameMeetsMinLength = form.name.trim().length >= 3
  const abbrevOk = form.abbreviation.trim().length >= 1
  const parentOk = Boolean(form.parentKey)
  const canSubmit = orgUUID && nameMeetsMinLength && abbrevOk && parentOk

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!canSubmit) return
    const parentRow =
      parentTypeOptions.find((r) => buyerTypeRowKey(r) === form.parentKey) ??
      defaultTypes.find((r) => buyerTypeRowKey(r) === form.parentKey)
    if (!parentRow) return
    const pa = getAttrs(parentRow)
    setSaving(true)
    const payload = {
      name: form.name.trim(),
      abbreviation: form.abbreviation.trim().replace(/\s+/g, '').toUpperCase(),
      parent: form.parentKey,
      category: inferBuyerTypeCategory(parentRow),
      complimentary: parentIsComplimentary(parentRow),
      organizationUUID: orgUUID,
      active: true,
      systemDefault: false,
      ...(editingId == null
        ? {
            sub_types: normalizeChannelEntries(pa.sub_types).map(
              ({ key, label, shortLabel }) => ({ key, label, shortLabel })
            ),
          }
        : {}),
    }
    const done = () => {
      setShowModal(false)
      setEditingId(null)
      const first = parentTypeOptions[0]
      setForm({
        name: '',
        abbreviation: '',
        parentKey: first ? buyerTypeRowKey(first) : '',
      })
      load()
    }
    const fail = (err) => console.error(err)
    if (editingId != null) {
      updateBuyerType(editingId, payload)
        .then(done)
        .catch(fail)
        .finally(() => setSaving(false))
    } else {
      createBuyerType(payload)
        .then(done)
        .catch(fail)
        .finally(() => setSaving(false))
    }
  }

  const handleDeactivate = (row) => {
    const a = getAttrs(row)
    if (
      !window.confirm(
        `Deactivate buyer type “${a.name ?? 'this type'}”? It will be hidden from lists but not removed from the database.`
      )
    ) {
      return
    }
    updateBuyerType(row.id, { active: false })
      .then(() => load())
      .catch((err) => console.error(err))
  }

  const renderDefaultCards = () => {
    if (defaultTypes.length === 0) return null
    return (
      <div className="buyer-types-defaults">
        <h2 className="buyer-types-defaults__heading">Default buyer types</h2>
        <p className="buyer-types-defaults__note">
          System defaults — cannot be edited or removed.
        </p>
        <div className="buyer-types-defaults__list">
          {defaultTypes.map((row) => {
            const a = getAttrs(row)
            const accent = accentForDefaultRow(a)
            const channels = normalizeChannelEntries(a.sub_types)
            const isStandard = String(a.name ?? '').toLowerCase() === 'standard'
            const tagAccent = isStandard ? STANDARD_TAG_ACCENT : accent
            return (
              <div
                key={row.id}
                className="buyer-types-default-card"
                style={{
                  '--bt-accent': accent,
                  '--bt-tag-accent': tagAccent,
                }}
              >
                <div className="buyer-types-default-card__accent" aria-hidden />
                <div className="buyer-types-default-card__body">
                  <div className="buyer-types-default-card__row">
                    <div className="buyer-types-default-card__main">
                      <div className="buyer-types-default-card__title-line">
                        <span className="buyer-types-default-card__title">
                          {displayBuyerTypeName(a.name)}
                        </span>
                        {isStandard ? (
                          <span className="buyer-types-default-card__system-badge">
                            System
                          </span>
                        ) : null}
                      </div>
                      {a.description ? (
                        <p className="buyer-types-default-card__desc">
                          {a.description}
                        </p>
                      ) : null}
                    </div>
                    {channels.length > 0 ? (
                      <div className="buyer-types-default-card__tags">
                        {channels.map((ch) => (
                          <span
                            key={ch.key}
                            className="buyer-types-default-card__tag"
                          >
                            {ch.label}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderCustomSection = () => (
    <div className="buyer-types-custom">
      <div className="buyer-types-panel__toolbar buyer-types-panel__toolbar--custom">
        <h2 className="buyer-types-section-heading">Custom buyer types</h2>
        <Button
          type="button"
          variant="primary"
          onClick={openAdd}
          disabled={!orgUUID}
        >
          + Add buyer type
        </Button>
      </div>
      {managedTypes.length === 0 ? (
        <div className="buyer-types-custom-empty">
          <p className="buyer-types-custom-empty__message">
            No custom buyer types yet
          </p>
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="px-4 rounded-pill"
            onClick={openAdd}
            disabled={!orgUUID}
          >
            + Add buyer type
          </Button>
        </div>
      ) : (
        <div className="buyer-types-table-wrap">
          <table className="buyer-types-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Code</th>
                <th scope="col">Parent</th>
                <th
                  scope="col"
                  className="buyer-types-table__actions"
                  aria-label="Actions"
                />
              </tr>
            </thead>
            <tbody>
              {managedTypes.map((row) => {
                const a = getAttrs(row)
                const parentMeta = resolveParentMeta(row, defaultTypes)
                return (
                  <tr key={row.id}>
                    <td className="buyer-types-table__name">{a.name}</td>
                    <td className="buyer-types-table__code">
                      {a.abbreviation || '—'}
                    </td>
                    <td>
                      <span
                        className="buyer-types-parent-pill"
                        style={{
                          backgroundColor: hexToRgba(parentMeta.color, 0.14),
                          color: parentMeta.color,
                        }}
                      >
                        {parentMeta.label}
                      </span>
                    </td>
                    <td className="buyer-types-table__actions">
                      <button
                        type="button"
                        className="buyer-types-table__icon-btn"
                        onClick={() => openEdit(row)}
                        aria-label={`Edit ${a.name ?? 'buyer type'}`}
                      >
                        <PencilIcon />
                      </button>
                      <button
                        type="button"
                        className="buyer-types-table__icon-btn"
                        onClick={() => handleDeactivate(row)}
                        aria-label={`Deactivate ${a.name ?? 'buyer type'}`}
                      >
                        <TrashIcon />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  if (isLoading) {
    return <PageLoadingContainer />
  }

  return (
    <>
      <section className="wrapper" data-testid="buyer-types-section">
        <header className="section-header">
          <div className="section-heading section-heading--secondary">
            <h1>Buyer types</h1>
          </div>
          <p className="section-header-desc">Manage buyer types</p>
        </header>

        {isLoadingData ? (
          <Card body className="card--sm text-center text-muted py-5">
            Loading…
          </Card>
        ) : systemDefaultRows.length === 0 && orgBuyerTypeRows.length === 0 ? (
          <Card body className="card--sm buyer-types-empty">
            <div className="buyer-types-empty__inner">
              <div className="buyer-types-empty__icon" aria-hidden>
                i
              </div>
              <p className="buyer-types-empty__message">
                No buyer types are available
              </p>
            </div>
          </Card>
        ) : isLoading ? (
          <LoadingContainer />
        ) : (
          <div className="buyer-types-layout">
            {renderDefaultCards()}
            {renderCustomSection()}
          </div>
        )}
      </section>

      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        className="buyer-types-modal"
        contentClassName="buyer-types-modal__content"
        dialogClassName="buyer-types-modal__dialog"
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton className="buyer-types-modal__header">
            <Modal.Title as="h2" className="buyer-types-modal__title">
              {editingId != null ? 'Edit buyer type' : 'Add custom buyer type'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="buyer-types-modal__body">
            <Form.Group
              className="buyer-types-modal__group"
              controlId="bt-name"
            >
              <Form.Label className="buyer-types-modal__label">Name</Form.Label>
              <Form.Control
                className="buyer-types-modal__control"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="e.g. Staff Discount, VIP Comp"
                required
                minLength={3}
                autoComplete="off"
              />
            </Form.Group>
            <Form.Group
              className="buyer-types-modal__group"
              controlId="bt-code"
            >
              <Form.Label className="buyer-types-modal__label">Code</Form.Label>
              <Form.Control
                className="buyer-types-modal__control buyer-types-modal__control--code"
                value={form.abbreviation}
                onChange={(e) =>
                  setForm((f) => ({ ...f, abbreviation: e.target.value }))
                }
                placeholder="e.g. STAFFDISC"
                required
                minLength={1}
                autoComplete="off"
                spellCheck={false}
              />
            </Form.Group>
            <Form.Group
              className="buyer-types-modal__group"
              controlId="bt-parent"
            >
              <Form.Label className="buyer-types-modal__label">
                Parent type
              </Form.Label>
              {parentTypeOptions.length === 0 ? (
                <p className="buyer-types-modal__parent-empty text-muted mb-0">
                  No system default parent types are available (excluding
                  Standard).
                </p>
              ) : (
                <div
                  className="buyer-types-modal__parent-segmented"
                  role="radiogroup"
                  aria-label="Parent type"
                >
                  {parentTypeOptions.map((row) => {
                    const a = getAttrs(row)
                    const key = buyerTypeRowKey(row)
                    const selected = form.parentKey === key
                    const accent = accentForDefaultRow(a)
                    const channels = normalizeChannelEntries(a.sub_types)
                    const subtitle = formatParentChannelsLine(channels)
                    return (
                      <button
                        key={key}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className={`buyer-types-modal__parent-segment${selected ? ' buyer-types-modal__parent-segment--selected' : ''}`}
                        style={{
                          '--bt-parent-accent': accent,
                        }}
                        onClick={() =>
                          setForm((f) => ({ ...f, parentKey: key }))
                        }
                      >
                        <span className="buyer-types-modal__parent-segment-title">
                          {displayBuyerTypeName(a.name)}
                        </span>
                        {subtitle ? (
                          <span className="buyer-types-modal__parent-segment-sub">
                            {subtitle}
                          </span>
                        ) : null}
                      </button>
                    )
                  })}
                </div>
              )}
            </Form.Group>
            {selectedParentRow ? (
              <div
                className="buyer-types-modal__info"
                role="note"
                style={{
                  '--bt-parent-accent': accentForDefaultRow(
                    getAttrs(selectedParentRow)
                  ),
                }}
              >
                {(() => {
                  const ch = normalizeChannelEntries(
                    getAttrs(selectedParentRow).sub_types
                  )
                  if (ch.length === 0) {
                    return <span>Channels match the selected parent type.</span>
                  }
                  return (
                    <span>
                      Available on{' '}
                      <strong>{formatChannelsReadableList(ch)}</strong>.
                      Channels inherited from parent.
                    </span>
                  )
                })()}
              </div>
            ) : null}
            <Button
              type="submit"
              className="buyer-types-modal__submit"
              disabled={saving || !canSubmit}
            >
              {saving
                ? editingId != null
                  ? 'Saving…'
                  : 'Adding…'
                : editingId != null
                  ? 'Save changes'
                  : 'Add buyer type'}
            </Button>
          </Modal.Body>
        </Form>
      </Modal>
    </>
  )
}
